import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert, Table, Select, Input, Tag, Button, message } from "antd";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsTransactions } from "../../utils/ApiEndpoints";
import { PageContainer, FilterRow, fmtRupees, fmtRupeesExact } from "../paUtils";
import CreatorDetailDrawer from "./CreatorDetailDrawer";
import styled from "styled-components";

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 20px 24px 16px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const STATUS_COLORS = {
  PAYMENT_SUCCESS: "green",
  PAYMENT_FAILED: "red",
  PAYMENT_PENDING: "gold",
  REFUNDED: "orange",
};

const SETTLEMENT_COLORS = {
  Settled: "green",
  Pending: "gold",
  Processing: "blue",
  Failed: "red",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "PAYMENT_SUCCESS", label: "Payment Success" },
  { value: "PAYMENT_FAILED", label: "Payment Failed" },
  { value: "PAYMENT_PENDING", label: "Payment Pending" },
  { value: "REFUNDED", label: "Refunded" },
];

const SETTLEMENT_OPTIONS = [
  { value: "", label: "All Settlements" },
  { value: "Settled", label: "Settled" },
  { value: "Pending", label: "Pending" },
  { value: "Processing", label: "Processing" },
  { value: "Failed", label: "Failed" },
];

function TransactionsTab({ startDate, endDate }) {
  const [status, setStatus] = useState("");
  const [settlementStatus, setSettlementStatus] = useState("");
  const [searchTxnId, setSearchTxnId] = useState("");
  const [searchOffering, setSearchOffering] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ transactions: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBpId, setSelectedBpId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { startDate, endDate, page, limit: 20 };
      if (status) params.status = status;
      if (settlementStatus) params.settlementStatus = settlementStatus;
      if (searchTxnId.trim()) params.transactionId = searchTxnId.trim();
      if (searchOffering.trim()) params.offeringName = searchOffering.trim();
      const res = await customClientAxios().get(PlatformAnalyticsTransactions, { params });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, status, settlementStatus, searchTxnId, searchOffering, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [startDate, endDate, status, settlementStatus, searchTxnId, searchOffering]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { startDate, endDate, page: 1, limit: 5000 };
      if (status) params.status = status;
      if (settlementStatus) params.settlementStatus = settlementStatus;
      if (searchTxnId.trim()) params.transactionId = searchTxnId.trim();
      if (searchOffering.trim()) params.offeringName = searchOffering.trim();
      const res = await customClientAxios().get(PlatformAnalyticsTransactions, { params });
      const rows = res.data.data?.transactions || [];
      if (!rows.length) {
        message.warning("No transactions to export");
        return;
      }

      const headers = [
        "Date", "Transaction ID", "Brand Page", "Slug", "Offering",
        "Customer Name", "Customer Email", "Amount", "Platform Fee",
        "Payout", "Status", "Settlement",
      ];
      const csvRows = rows.map((r) => [
        r.saleDate ? new Date(r.saleDate).toLocaleDateString("en-IN") : "",
        r.transactionId || "",
        (r.brandPageName || "").replace(/,/g, ""),
        r.brandPageSlug || "",
        (r.featureName || "").replace(/,/g, ""),
        (r.bookingCustomerDetails?.name || "").replace(/,/g, ""),
        r.bookingCustomerDetails?.email || "",
        fmtRupeesExact(r.paymentBreakdown?.totalAmount),
        fmtRupeesExact(r.paymentBreakdown?.platformFee),
        fmtRupeesExact(r.payoutAmount),
        r.status?.replace(/_/g, " ") || "",
        r.settlement?.status || "",
      ]);

      const csv = [headers, ...csvRows].map((row) => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `transactions_${startDate}_${endDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      message.success(`Exported ${rows.length} transactions`);
    } catch (e) {
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "saleDate",
      key: "saleDate",
      width: 100,
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 140,
      render: (v) => (
        <span style={{ fontSize: 12, fontFamily: "monospace", color: "#64748b" }}>
          {v ? `${v.slice(0, 12)}...` : "—"}
        </span>
      ),
    },
    {
      title: "Brand Page",
      key: "brandPage",
      render: (_, r) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); setSelectedBpId(r.brandPageId); setDrawerOpen(true); }}
        >
          <div style={{ fontWeight: 600, color: "#6366f1", fontSize: 13 }}>{r.brandPageName}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.brandPageSlug}</div>
        </div>
      ),
    },
    { title: "Offering", dataIndex: "featureName", key: "featureName", ellipsis: true },
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{r.bookingCustomerDetails?.name}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.bookingCustomerDetails?.email}</div>
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, r) => (
        <span style={{ fontWeight: 700, color: "#10b981" }}>
          {fmtRupees(r.paymentBreakdown?.totalAmount)}
        </span>
      ),
    },
    {
      title: "Platform Fee",
      key: "fee",
      render: (_, r) => fmtRupees(r.paymentBreakdown?.platformFee),
    },
    {
      title: "Payout",
      dataIndex: "payoutAmount",
      key: "payoutAmount",
      render: (v) => <span style={{ color: "#6366f1", fontWeight: 600 }}>{fmtRupees(v)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={STATUS_COLORS[v] || "default"} style={{ fontSize: 11 }}>
          {v?.replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Settlement",
      key: "settlement",
      render: (_, r) => r.settlement?.status ? (
        <Tag color={SETTLEMENT_COLORS[r.settlement.status] || "default"} style={{ fontSize: 11 }}>
          {r.settlement.status}
        </Tag>
      ) : "—",
    },
  ];

  const pagination = data.pagination || {};

  return (
    <PageContainer>
      <FilterRow>
        <Input
          placeholder="Transaction ID..."
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          value={searchTxnId}
          onChange={(e) => setSearchTxnId(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="Offering name..."
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          value={searchOffering}
          onChange={(e) => setSearchOffering(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          style={{ width: 180 }}
          placeholder="Sale status"
        />
        <Select
          options={SETTLEMENT_OPTIONS}
          value={settlementStatus}
          onChange={setSettlementStatus}
          style={{ width: 180 }}
          placeholder="Settlement status"
        />
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
          style={{ marginLeft: "auto" }}
        >
          Export CSV
        </Button>
      </FilterRow>

      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <TableCard>
        <TableHeader>
          Transactions{pagination.total ? ` (${pagination.total})` : ""}
        </TableHeader>
        <Spin spinning={loading}>
          <Table
            dataSource={data.transactions || []}
            columns={columns}
            rowKey="transactionId"
            size="middle"
            pagination={{
              current: page,
              pageSize: 20,
              total: pagination.total || 0,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (total) => `${total} transactions`,
            }}
            scroll={{ x: 1100 }}
          />
        </Spin>
      </TableCard>

      <CreatorDetailDrawer
        brandPageId={selectedBpId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </PageContainer>
  );
}

export default TransactionsTab;
